/**
 * Room folder (ROOM group) helpers — identifier scheme and centroid math.
 *
 * A room folder is a ROOM-type group nested under a FLOOR group that files the
 * sketches placed inside that room. Auto-created folders derive their id from
 * the room annotation (`roomgroup_<roomAnnotationId>`) so the room↔folder
 * mapping stays computable and creation stays idempotent; manually created
 * folders use the `roomgroup_manual_` prefix so reconciliation never touches
 * them. The authoritative link is the `room_group_link` property on the room
 * annotation (see RoomGeometryPropertyGetters in potree-shared).
 */

import type { Coordinate } from '@pointorama/potree-shared';

/**
 * Payload of the `room_tag_edit` scene event — opens the inline viewport name
 * editor for a room tag (RoomTagNameEditor). Dispatched by the RoomTagTool
 * when an existing tag label is clicked, and by the tag-creation handler right
 * after a new tag is requested.
 */
export interface RoomTagEditRequest {
  folderId: string;
  /** Current name, pre-filled and fully selected in the editor. */
  name: string;
  /** World position of the tag anchor the editor floats over. */
  position: { x: number; y: number; z: number };
  /**
   * Resolves once the folder create settled (new tags): `true` when it exists
   * server-side, `false` when the create FAILED. A rename typed before the
   * create confirms must await it — and must NOT be sent when it resolves
   * false (the folder doesn't exist to rename).
   */
  ready?: Promise<boolean>;
}

// The id scheme lives in shared-types — the server's delete cascade derives
// the same ids and the convention must have exactly one owner.
export { roomFolderIdForRoom, manualRoomFolderId, roomIdFromAutoFolderId } from 'shared-types';

/**
 * Resolve an annotation's FLOOR group id from its (possibly nested) group:
 * annotations filed in a room folder resolve to the folder's parent floor.
 * Runtime floor-scoped logic (blacklists, glyph filters, tools) must use this
 * instead of reading `groupIdentifier` directly once room folders exist.
 */
export function resolveFloorGroupIdentifier(
  groups: readonly { identifier: string; parentIdentifier?: string | null }[],
  groupIdentifier: string | null | undefined,
): string | null {
  if (!groupIdentifier) return null;
  const group = groups.find((g) => g.identifier === groupIdentifier);
  return group?.parentIdentifier ?? groupIdentifier;
}

/**
 * Vertex-average centroid of a room polygon — matches the anchor
 * PointoramaRoom uses for its area label, so folder position and label agree.
 */
export function polygonCentroid(points: Coordinate[]): Coordinate | null {
  if (points.length === 0) return null;
  const sum = { x: 0, y: 0, z: 0 };
  for (const p of points) {
    sum.x += p.x;
    sum.y += p.y;
    sum.z += p.z;
  }
  return { x: sum.x / points.length, y: sum.y / points.length, z: sum.z / points.length };
}
